import React, { useMemo } from "react";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import type { AgentFilterTaxonomyOption } from "../hooks/useAgentFilterOptions";
import { NEUTRAL, RADIUS, VIOLET } from "../styles/brandTokens";

export type TaxonomyGroup = [string, AgentFilterTaxonomyOption[]];

export interface TaxonomySelection {
  l1: string;
  l2: string | null;
}

/**
 * Groups stay visible when the query hits the group name (every option in it)
 * or any option label. A group the query misses drops out entirely.
 */
export function visibleTaxonomyGroups(groups: TaxonomyGroup[], query: string): TaxonomyGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return groups;
  const visible: TaxonomyGroup[] = [];
  for (const [l1, options] of groups) {
    if (l1.toLowerCase().includes(needle)) {
      visible.push([l1, options]);
      continue;
    }
    const matched = options.filter((option) => {
      const leaf = (option.l2 ?? "").toLowerCase();
      return leaf.includes(needle) || option.label.toLowerCase().includes(needle);
    });
    if (matched.length > 0) visible.push([l1, matched]);
  }
  return visible;
}

/** Keep the open group when it still matches; otherwise the first visible group. */
export function openTaxonomyGroup(visible: TaxonomyGroup[], group: string | null): string | null {
  if (group && visible.some(([l1]) => l1 === group)) return group;
  return visible[0]?.[0] ?? null;
}

function isPicked(selected: TaxonomySelection[], option: AgentFilterTaxonomyOption): boolean {
  return selected.some((item) => item.l1 === option.l1 && (item.l2 ?? null) === (option.l2 ?? null));
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${RADIUS.control}px`,
    backgroundColor: NEUTRAL.surface,
    fontSize: 14,
    "& fieldset": { borderColor: NEUTRAL.border },
    "&:hover fieldset": { borderColor: NEUTRAL.borderStrong },
  },
} as const;

export const TaxonomyBrowser: React.FC<{
  groups: TaxonomyGroup[];
  query: string;
  onQuery: (value: string) => void;
  group: string | null;
  onGroup: (l1: string) => void;
  selected: TaxonomySelection[];
  onToggle: (l1: string, l2: string | null) => void;
}> = ({ groups, query, onQuery, group, onGroup, selected, onToggle }) => {
  const visible = useMemo(() => visibleTaxonomyGroups(groups, query), [groups, query]);
  const open = openTaxonomyGroup(visible, group);
  const options = visible.find(([l1]) => l1 === open)?.[1] ?? [];

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder="Search categories"
        inputProps={{ "aria-label": "Search Theo categories" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: NEUTRAL.textMuted }} />
            </InputAdornment>
          ),
        }}
        sx={fieldSx}
      />
      {visible.length === 0 ? (
        <Typography sx={{ mt: 1.25, fontSize: 12.5, color: NEUTRAL.textMuted }}>
          No categories match that search.
        </Typography>
      ) : (
        <Box
          sx={{
            mt: 1.25,
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Box
            role="listbox"
            aria-label="Category groups"
            sx={{
              flex: { sm: "0 0 240px" },
              width: { xs: "100%", sm: 240 },
              maxHeight: 280,
              overflowY: "auto",
              border: `1px solid ${NEUTRAL.border}`,
              borderRadius: `${RADIUS.control}px`,
              backgroundColor: NEUTRAL.subtle,
            }}
          >
            {visible.map(([l1]) => {
              const active = l1 === open;
              const count = selected.filter((item) => item.l1 === l1).length;
              return (
                <Box
                  key={l1}
                  component="button"
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => onGroup(l1)}
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 1.25,
                    py: 0.85,
                    border: 0,
                    borderBottom: `1px solid ${NEUTRAL.border}`,
                    cursor: "pointer",
                    textAlign: "left",
                    font: "inherit",
                    fontSize: 12.5,
                    fontWeight: active ? 600 : 500,
                    color: active ? VIOLET.theoText : NEUTRAL.text,
                    backgroundColor: active ? VIOLET.tint : "transparent",
                    "&:hover": { backgroundColor: active ? VIOLET.tint : NEUTRAL.hover },
                  }}
                >
                  <Box component="span" sx={{ minWidth: 0 }}>
                    {l1}
                  </Box>
                  {count > 0 ? (
                    <Box
                      component="span"
                      sx={{
                        flex: "0 0 auto",
                        minWidth: 18,
                        px: 0.6,
                        borderRadius: `${RADIUS.control}px`,
                        backgroundColor: VIOLET.main,
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: "18px",
                        textAlign: "center",
                      }}
                    >
                      {count}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ mb: 0.75, fontSize: 12, fontWeight: 600, color: NEUTRAL.textSecondary }}>
              {open}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, maxHeight: 252, overflowY: "auto" }}>
              {options.map((option) => {
                const picked = isPicked(selected, option);
                const label = option.l2 ? option.l2 : `All of ${option.l1}`;
                return (
                  <Chip
                    key={`${option.l1}|${option.l2 ?? ""}`}
                    label={label}
                    size="small"
                    onClick={() => onToggle(option.l1, option.l2)}
                    aria-pressed={picked}
                    sx={{
                      borderRadius: `${RADIUS.control}px`,
                      fontSize: 12.5,
                      backgroundColor: picked ? VIOLET.tint : NEUTRAL.subtle,
                      color: picked ? VIOLET.theoText : NEUTRAL.textSecondary,
                      border: `1px solid ${picked ? VIOLET.main : NEUTRAL.border}`,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
